/** Core Libs */
import Head from 'next/head';
import { useEffect } from 'react';
import type { GetServerSideProps, NextPage } from 'next';
import * as Sentry from '@sentry/nextjs';

/** Services */
import { getArticle, increaseNumberOfViews } from '@services/article.service';

/** Components */
import { MetaTags } from '@helpers/meta-tags.helper';
import MainLayout from '@layouts/main-layout/main-layout';
import ArticlePage from '@components/blog/article-page/article-page';
import { IArticle, IArticleRating } from '@models/article.interface';
import { getMatchingRedirect } from '@services/redirect.service';
import { ServerSidePropsError, throwServerSidePropsError } from '@helpers/server-side-props-error.helper';
import { getNoIndexMetaTag } from '@libs/utils/article.utils';

interface PostProps {
  article: IArticle;
  articleRating: IArticleRating;
}

const Post: NextPage<PostProps> = ({ article, articleRating }) => {
  useEffect(() => {
    increaseNumberOfViews(article?.slug).catch((error) => {
      Sentry.captureException(error, {
        tags: { source: 'increaseNumberOfViews', type: error?.type, online: error?.cause?.online },
        extra: { slug: article?.slug, ...error?.cause },
      });
    });
  }, [article?.slug]);

  return (
    <>
      <Head>
        {MetaTags({
          title: article.title,
          description: article.metaDescription,
          imageUrl: article.posterUrl,
        })}
        {getNoIndexMetaTag(article.slug)}
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <MainLayout>
        <ArticlePage article={article} articleRating={articleRating} />
      </MainLayout>
    </>
  );
};

export default Post;

export const getServerSideProps: GetServerSideProps<PostProps | ServerSidePropsError> = async (context) => {
  const id = context.query.id as string;
  const { data } = await getArticle(id);

  if (!!data?.article?.deletedAt) {
    return throwServerSidePropsError(
      context,
      410,
      'Article is gone.',
      'Article has been permanently deleted. Use nav above or',
    );
  }

  if (data) {
    return {
      props: {
        article: data.article,
        articleRating: data.rating,
      },
    };
  }

  const [url] = context.resolvedUrl.split('?');
  const redirectUrl = await getMatchingRedirect(url);
  if (redirectUrl) {
    return {
      redirect: {
        destination: redirectUrl,
        permanent: true,
      },
    };
  }

  return {
    notFound: true,
  };
};
